import React, { useState } from "react";
import { TreeTable } from "primereact/treetable";
import { Ripple } from "primereact/ripple";

import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import LinkButton from "../shared/LinkButton";
import CustomIcon from "../shared/CustomIcon";
import { format } from "date-fns";
import refreshIcon from "../../assets/icons/refresh.svg";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { classNames } from "primereact/utils";
import { changeSearchCriteria, getReportData } from "./state/actions";
import { useKeycloak } from "@react-keycloak/web";
import useSnackBar from "../Hooks/useSnackBar";

const paginatorTemplate = {
  layout: "RowsPerPageDropdown PrevPageLink PageLinks NextPageLink",
  PageLinks: (options) => {
    const [searchParams, setSearchParams] = useSearchParams();

    if (
      (options.view.startPage === options.page &&
        options.view.startPage !== 0) ||
      (options.view.endPage === options.page &&
        options.page + 1 !== options.totalPages)
    ) {
      const className = classNames(options.className, { "p-disabled": true });

      return (
        <span className={className} style={{ userSelect: "none" }}>
          ...
        </span>
      );
    }

    const handleClick = (e) => {
      options.onClick(e);
      searchParams.set("pageNumber", options.page + 1);
      setSearchParams(searchParams);
    };

    return (
      <button type="button" className={options.className} onClick={handleClick}>
        {options.page + 1}
        <Ripple />
      </button>
    );
  },
  RowsPerPageDropdown: (options) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const value = +searchParams.get("pageSize") || 10;
    const reportData = useSelector((state) => state.reporting.reportData);
    const totalItems = reportData?.totalElement || 0;

    const handleDropdownChange = (e) => {
      options.onChange(e);
      searchParams.set("pageSize", e.value);
      setSearchParams(searchParams);
    };

    return (
      <div className="reporting-show-dropdown">
        <span>Show: </span>
        <Dropdown
          value={value}
          options={options.options}
          onChange={handleDropdownChange}
        />
        <span>{`of ${totalItems} Results `}</span>
      </div>
    );
  },
};

const PaginatorLeftContent = () => {
  const { state } = useLocation();
  const { error, success } = useSnackBar();

  const dispatch = useDispatch();
  const { reportId } = useParams();
  const { keycloak } = useKeycloak();
  const userId = keycloak.tokenParsed.preferred_username;
  const id = state?.isCorzapReports ? "default" : userId;

  const searchCriteria = useSelector((state) => state.reporting.searchCriteria);

  const [searchParams] = useSearchParams();
  const pageNumber = +searchParams.get("pageNumber") || 1;
  const pageSize = +searchParams.get("pageSize") || 10;

  const updatedAt = new Date();

  const handleRefresh = () => {
    dispatch(
      getReportData(
        id,
        reportId,
        {
          pageSize,
          pageNumber,
          searchCriteria: {
            ...searchCriteria,
          },
        },
        success,
        error
      )
    );
  };

  return (
    <div className="pagination--section footer--last-refreshed">
      <span>{`Last refreshed ${format(
        updatedAt,
        "MM-dd-yyyy hh:mm:ss"
      )}`}</span>
      <LinkButton
        startIcon={
          <span className="footer--refresh-icon">
            <CustomIcon svgIcon={refreshIcon} />
          </span>
        }
        onClick={handleRefresh}
      >
        Refresh
      </LinkButton>
    </div>
  );
};

let timer;

export default function Table({ reportData, columns }) {
  const dispatch = useDispatch();
  const searchCriteria = useSelector((state) => state.reporting.searchCriteria);
  const [selectedNodeKey, setSelectedNodeKey] = useState(null);
  const [searchParams] = useSearchParams();
  const pageSize = +searchParams.get("pageSize") || 10;

  const dto = reportData?.dto;
  const totalElement = dto?.totalElement || 10;

  const createTreeNodes = (group, level) => {
    const treeNodes = [];

    Object.entries(group || {}).forEach(([key, value]) => {
      const { countOfMessages, avg, totalAmount } = value;

      const node = {
        key: `${level}-${key}`,
        data: {
          [level]: key,
          countOfMessages,
          avg,
          totalAmount,
        },
        children: [],
      };

      if (value.group) {
        node.children = createTreeNodes(value.group, value.groupLevel);
      }

      if (value.records) {
        value.records.forEach((payload, payloadIndex) => {
          // convert all keys to lowercase
          const keys = Object.keys(payload);
          const lowerCaseKeys = keys.reduce((acc, key) => {
            acc[key] = payload[key];
            return acc;
          }, {});

          const { details, ...rest } = lowerCaseKeys;
          const payloadNode = {
            key: `${level}-${key}-${payloadIndex}`,
            data: {
              ...rest,
              ...details,
              countOfMessages: "N/A",
              avg: "N/A",
              totalAmount: "N/A",
            },
            children: [],
          };

          node.children.push(payloadNode);
        });
      }

      treeNodes.push(node);
    });
    return treeNodes;
  };

  const createTreeNodesFromRecords = (records) => {
    const treeNodes = [];
    records.forEach((record) => {
      const keys = Object.keys(record);
      const lowerCaseKeys = keys.reduce((acc, key) => {
        acc[key] = record[key];
        return acc;
      }, {});

      const { details, ...rest } = lowerCaseKeys;

      const node = {
        key: record.transactionId,
        data: { ...rest, ...details },
        children: [],
      };

      treeNodes.push(node);
    });
    return treeNodes;
  };

  const groupLevel = dto?.groupSegment?.groupLevel;
  const group = dto?.groupSegment?.group;

  const treeTableNodes =
    "records" in dto?.groupSegment
      ? createTreeNodesFromRecords(dto?.groupSegment?.records)
      : createTreeNodes(group, groupLevel);

  const isEmpty = !reportData || !dto || !columns;

  return (
    <div className="card">
      {!isEmpty ? (
        <TreeTable
          value={treeTableNodes}
          tableStyle={{ minWidth: "50rem" }}
          tableClassName="reporting-treetable"
          paginator
          rows={pageSize}
          rowsPerPageOptions={[10, 50, 100]}
          paginatorTemplate={paginatorTemplate}
          paginatorLeft={PaginatorLeftContent}
          totalRecords={totalElement}
          scrollable
          scrollHeight="480px"
          selectionMode="single"
          selectionKeys={selectedNodeKey}
          onSelectionChange={(e) => {
            setSelectedNodeKey(e.value);
          }}
          filterMode="strict"
          sortMode="multiple"
        >
          {columns
            ?.filter((col) => col.key !== "messageMetadata")
            .map((column, index) => (
              <Column
                key={column?.key || index}
                field={column?.key}
                header={column.title}
                expander={index === 0}
                style={{
                  width: "280px",
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                sortable
                filter
                filterPlaceholder={`Filter by ${column.title.toLowerCase()}`}
                filterElement={
                  <input
                    class="p-inputtext p-component p-column-filter p-column-filter"
                    placeholder={`Filter by ${column.title.toLowerCase()}`}
                    value={(searchCriteria[column?.key] || []).join(",")}
                    onChange={(e) => {
                      const value = e.target.value;
                      dispatch(
                        changeSearchCriteria({
                          [column?.key]: value ? value?.split(",") : [],
                        })
                      );
                    }}
                  />
                }
              />
            ))}

          <Column />
        </TreeTable>
      ) : (
        <></>
      )}
    </div>
  );
}
